import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { transformAnonymityInfo } from "../../../utils/helpers";
import { collectServerSideState } from "../../../utils/helpers/collectServerSideState";

import { useAnonymityInfo } from "../../../hooks";

import { Container, WidgetTable } from "../../../components";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { Seo } from "../../../components/Seo/Seo";
import { Faq } from "../../../components/common/Faq/Faq";
import HeadUpperSeo from "../../../components/common/HeadUpperSeo";
import { WidgetStatus } from "../../../components/common/WidgetStatus/WidgetStatus";
import { HostInfo } from "./components/HostInfo/HostInfo";
import { UserAdditionalInfo } from "./components/UserAdditionalInfo/UserAdditionalInfo";

import { Section } from "../../../components/common/Section/Section.styled";
import {
  GridContainer,
  GridItemLeft,
  GridItemRight,
  Wrapper,
} from "./MyAnonymity.styled";

const MyAnonymity = () => {
  const { t } = useTranslation();

  const [showLoader, setShowLoader] = useState(true);
  const [showButton, setShowButton] = useState(true);
  const widgetTableRef = useRef(null);

  const { anonymityInfo, blackList, webRtcStatus, ips } = useAnonymityInfo(
    setShowLoader,
    t,
  );
  const smallIps = ips?.map(item => item?.ip);

  const anonymityInfoAndBlackListAndWebRtc = transformAnonymityInfo(
    anonymityInfo,
    blackList,
    webRtcStatus,
    smallIps,
  );

  const isWebRtc = anonymityInfoAndBlackListAndWebRtc?.webRtc === true;
  const isReplaceUserAgent =
    anonymityInfoAndBlackListAndWebRtc?.userAgent !==
    anonymityInfoAndBlackListAndWebRtc?.userAgentJs;

  const anonymityStatus = !isWebRtc && isReplaceUserAgent;

  const isShowErrorDescription = () => {
    if (isWebRtc && !isReplaceUserAgent) {
      return (
        <>
          {t("webrtcCheck.notProtectedDescription")} <br />
          {t("anonymityCheck.notProtectedDescription")}
        </>
      );
    }
    if (isWebRtc) return t("webrtcCheck.notProtectedDescription");
    if (!isReplaceUserAgent) return t("anonymityCheck.notProtectedDescription");
  };

  const startLoader = () => {
    if (widgetTableRef.current) {
      widgetTableRef.current.scrollIntoView({ behavior: "smooth" });
    }
    setShowLoader(true);
  };

  const restartTest = () => {
    window.location.reload();
    // startLoader();
  };

  const startTest = () => {
    startLoader();
    setShowButton(false);
  };

  return (
    <>
      <Section variant="start" container ref={widgetTableRef}>
        <Container>
          <HeadUpperSeo />

          <Wrapper>
            {showLoader ? (
              <LoadingSpinner onFinish={() => setShowLoader(false)} />
            ) : (
              <GridContainer showButton={showButton}>
                <GridItemLeft>
                  <WidgetTable
                    backData={anonymityInfo}
                    params={[
                      "ip",
                      "host",
                      "country",
                      "city",
                      "zipcode",
                      "coordinates",
                    ]}
                    storedIp={anonymityInfo?.ip}
                    button={
                      showButton && t("anonymityCheck.buttonAnonymityCheck")
                    }
                    type="anonymity"
                    onButtonClick={startTest}
                  />
                </GridItemLeft>
                {!showButton && (
                  <GridItemRight>
                    <WidgetStatus
                      height="100%"
                      status={anonymityStatus ? "success" : "error"}
                      title={
                        anonymityStatus
                          ? t("webrtcCheck.protected")
                          : t("webrtcCheck.notProtect")
                      }
                      description={
                        anonymityStatus
                          ? t("webrtcCheck.protectedDescription")
                          : isShowErrorDescription()
                      }
                    />
                  </GridItemRight>
                )}
              </GridContainer>
            )}
            {!showLoader && !showButton && (
              <>
                <UserAdditionalInfo
                  backData={anonymityInfo}
                  showButton={showButton}
                  restartTest={restartTest}
                />
                <HostInfo backData={anonymityInfoAndBlackListAndWebRtc} />
              </>
            )}
          </Wrapper>
        </Container>
      </Section>
      <Faq sectionType="innerTools" />
      <Seo />
    </>
  );
};

MyAnonymity.getServerSideState = async (store, params) => {
  await collectServerSideState(store, params);
};

export default MyAnonymity;
